import { normalizeLogHours } from "./labourCost";
import { computeActualCostFromLedgers } from "./projectFinancialReport";
import { isProjectArchived } from "./projectVisibility";

export function computeTotalMaterialCost(usageRows = []) {
  return usageRows.reduce(
    (sum, row) => sum + Number(row.total_cost || 0),
    0,
  );
}

function activityDate(value) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

function getLatestActivityDate(logs = [], usageRows = [], otherCosts = []) {
  let latest = null;

  function consider(value) {
    const date = activityDate(value);
    if (date && (!latest || date > latest)) {
      latest = date;
    }
  }

  logs.forEach((log) => consider(log.date_iso));
  usageRows.forEach((row) => consider(row.usage_date));
  otherCosts.forEach((row) => consider(row.created_at));

  return latest;
}

export function filterProjectsForAnalytics(
  projects = [],
  { projectStatus = "active", clientId = "", projectId = "" } = {},
) {
  return projects.filter((project) => {
    if (projectId && project.project_id !== projectId) {
      return false;
    }

    if (clientId && project.client_id !== clientId) {
      return false;
    }

    const archived = isProjectArchived(project);

    if (projectStatus === "active" && archived) {
      return false;
    }

    if (projectStatus === "archived" && !archived) {
      return false;
    }

    return true;
  });
}

export function buildProjectAnalyticsReport({
  projects = [],
  timeLogs = [],
  materialUsage = [],
  otherCosts = [],
}) {
  const logsByProject = {};

  timeLogs.forEach((log) => {
    const projectId = log.project_id;

    if (!projectId) {
      return;
    }

    if (!logsByProject[projectId]) {
      logsByProject[projectId] = [];
    }

    logsByProject[projectId].push(log);
  });

  const usageByProject = {};

  materialUsage.forEach((row) => {
    const projectId = row.project_id;

    if (!projectId) {
      return;
    }

    if (!usageByProject[projectId]) {
      usageByProject[projectId] = [];
    }

    usageByProject[projectId].push(row);
  });

  const otherByProject = {};

  otherCosts.forEach((row) => {
    const projectId = row.project_id;

    if (!projectId || row.is_active === false) {
      return;
    }

    if (!otherByProject[projectId]) {
      otherByProject[projectId] = [];
    }

    otherByProject[projectId].push(row);
  });

  const rows = [];

  projects.forEach((project) => {
    const projectId = project.project_id;
    const logs = logsByProject[projectId] || [];
    const usage = usageByProject[projectId] || [];
    const projectOtherCosts = otherByProject[projectId] || [];
    const totalHours = logs.reduce(
      (sum, log) => sum + normalizeLogHours(log),
      0,
    );
    const { labourCost, materialCost, otherCost, actualCost } =
      computeActualCostFromLedgers(usage, logs, projectOtherCosts);
    const totalCost = actualCost;

    if (totalHours <= 0 && materialCost <= 0 && otherCost <= 0) {
      return;
    }

    rows.push({
      project_id: projectId,
      project_name: project.project_name || "Unnamed Project",
      project_number: project.project_number || null,
      client_name: project.clients?.client_name || null,
      client_id: project.client_id || null,
      isArchived: isProjectArchived(project),
      totalHours,
      labourCost,
      materialCost,
      otherCost,
      totalCost,
      lastActivity: getLatestActivityDate(logs, usage, projectOtherCosts),
    });
  });

  return rows;
}

export const PROJECT_ANALYTICS_SORT_OPTIONS = [
  { value: "highest_total_cost", label: "Highest Total Cost" },
  { value: "highest_margin", label: "Highest Margin" },
  { value: "lowest_margin", label: "Lowest Margin" },
  { value: "highest_billed", label: "Highest Billed" },
  { value: "most_hours", label: "Most Hours" },
  { value: "highest_material_cost", label: "Highest Material Cost" },
  { value: "most_recent", label: "Most Recent Activity" },
  { value: "name_asc", label: "Project Name (A–Z)" },
];

export function sortProjectAnalyticsRows(rows, sortKey = "highest_total_cost") {
  const sorted = [...rows];

  switch (sortKey) {
    case "highest_margin":
      return sorted.sort((a, b) => Number(b.margin || 0) - Number(a.margin || 0));
    case "lowest_margin":
      return sorted.sort((a, b) => Number(a.margin || 0) - Number(b.margin || 0));
    case "highest_billed":
      return sorted.sort((a, b) => Number(b.billed || 0) - Number(a.billed || 0));
    case "most_hours":
      return sorted.sort((a, b) => b.totalHours - a.totalHours);
    case "highest_material_cost":
      return sorted.sort((a, b) => b.materialCost - a.materialCost);
    case "most_recent":
      return sorted.sort((a, b) => {
        if (!a.lastActivity && !b.lastActivity) return 0;
        if (!a.lastActivity) return 1;
        if (!b.lastActivity) return -1;
        return b.lastActivity.localeCompare(a.lastActivity);
      });
    case "name_asc":
      return sorted.sort((a, b) =>
        a.project_name.localeCompare(b.project_name),
      );
    case "highest_total_cost":
    default:
      return sorted.sort((a, b) => b.totalCost - a.totalCost);
  }
}

export function computeReportGrandTotals(rows = []) {
  return rows.reduce(
    (acc, row) => ({
      projectCount: acc.projectCount + 1,
      totalHours: acc.totalHours + row.totalHours,
      labourCost: acc.labourCost + row.labourCost,
      materialCost: acc.materialCost + row.materialCost,
      otherCost: acc.otherCost + Number(row.otherCost || 0),
      totalCost: acc.totalCost + row.totalCost,
      quoted: acc.quoted + Number(row.quoted || 0),
      billed: acc.billed + Number(row.billed || 0),
      margin: acc.margin + Number(row.margin || 0),
    }),
    {
      projectCount: 0,
      totalHours: 0,
      labourCost: 0,
      materialCost: 0,
      otherCost: 0,
      totalCost: 0,
      quoted: 0,
      billed: 0,
      margin: 0,
    },
  );
}
